import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, Plus, Trash, Coins, Clock, PaperPlaneTilt, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = ['OnlyFans', 'Twitter', 'Telegram', 'WhatsApp', 'Messenger', 'Instagram', 'Snapchat', 'Other'];

const ExternalSubsTab = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [section, setSection] = useState('products'); // products | orders
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deliveringId, setDeliveringId] = useState(null);
  const [deliverLink, setDeliverLink] = useState('');
  const [deliverMsg, setDeliverMsg] = useState('');

  const [form, setForm] = useState({
    platform: 'OnlyFans', title: '', description: '',
    price_tokens: 100, duration_days: 7, auto_link: '',
  });

  const load = async () => {
    try {
      const [p, o] = await Promise.all([
        axios.get(`${API}/api/store/external-subs/mine`, { withCredentials: true }),
        axios.get(`${API}/api/store/external-subs/orders/incoming`, { withCredentials: true }),
      ]);
      setProducts(p.data);
      setOrders(o.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || form.price_tokens < 1) {
      toast.error('Title and price required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/store/external-subs`, form, { withCredentials: true });
      toast.success('External sub created');
      setOpen(false);
      setForm({ platform: 'OnlyFans', title: '', description: '', price_tokens: 100, duration_days: 7, auto_link: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this external sub product?')) return;
    try {
      await axios.delete(`${API}/api/store/external-subs/${id}`, { withCredentials: true });
      load();
    } catch { toast.error('Delete failed'); }
  };

  const deliver = async (orderId) => {
    if (!deliverLink.trim()) { toast.error('Enter the access link'); return; }
    try {
      await axios.post(`${API}/api/store/external-subs/orders/${orderId}/deliver`,
        { access_link: deliverLink, message: deliverMsg }, { withCredentials: true });
      toast.success('Link delivered');
      setDeliveringId(null); setDeliverLink(''); setDeliverMsg('');
      load();
    } catch { toast.error('Delivery failed'); }
  };

  const pendingCount = orders.filter(o => !o.delivered).length;

  return (
    <div className="space-y-4" data-testid="external-subs-tab">
      <div className="flex gap-1 bg-slate-900/50 rounded-xl p-1 w-fit">
        <button onClick={() => setSection('products')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${section === 'products' ? 'bg-blue-500/20 text-blue-400' : 'text-slate-500 hover:text-white'}`}
          data-testid="section-products">
          My Products ({products.length})
        </button>
        <button onClick={() => setSection('orders')}
          className={`px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 ${section === 'orders' ? 'bg-amber-500/20 text-amber-400' : 'text-slate-500 hover:text-white'}`}
          data-testid="section-orders">
          Incoming Orders
          {pendingCount > 0 && <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">{pendingCount}</span>}
        </button>
      </div>

      {section === 'products' && (
        <>
          <div className="flex justify-end">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button className="px-4 py-2 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 flex items-center gap-2"
                  data-testid="create-external-sub-btn">
                  <Plus className="w-5 h-5" /> New External Sub
                </button>
              </DialogTrigger>
              <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create External Subscription</DialogTitle>
                  <p className="text-xs text-slate-400">Sell paid access to your OnlyFans, Telegram, Twitter, WhatsApp, etc. Link is delivered after purchase.</p>
                </DialogHeader>
                <div className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Platform</Label>
                      <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })}
                        className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-white">
                        {PLATFORMS.map(p => <option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label>Duration (days)</Label>
                      <Input type="number" min="1" value={form.duration_days}
                        onChange={e => setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })}
                        className="bg-slate-800 border-white/10" />
                    </div>
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                      placeholder="e.g., Weekly OnlyFans Access"
                      className="bg-slate-800 border-white/10" data-testid="ext-title" />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                      placeholder="What do buyers get?" className="bg-slate-800 border-white/10" />
                  </div>
                  <div>
                    <Label>Price (tokens)</Label>
                    <Input type="number" min="1" value={form.price_tokens}
                      onChange={e => setForm({ ...form, price_tokens: parseInt(e.target.value) || 0 })}
                      className="bg-slate-800 border-white/10" data-testid="ext-price" />
                  </div>
                  <div>
                    <Label>Auto-delivered link (optional)</Label>
                    <Input value={form.auto_link} onChange={e => setForm({ ...form, auto_link: e.target.value })}
                      placeholder="https://onlyfans.com/yourname"
                      className="bg-slate-800 border-white/10" />
                    <p className="text-xs text-slate-600 mt-1">
                      If set, buyers get this link instantly. Leave blank to deliver a personalized link per buyer.
                    </p>
                  </div>
                  <button onClick={create} disabled={saving}
                    className="w-full py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 disabled:opacity-50"
                    data-testid="save-ext-btn">
                    {saving ? 'Saving…' : 'Create'}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {products.length === 0 ? (
            <div className="glass-light rounded-2xl p-12 text-center">
              <Link className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">No external subs yet. Sell paid access to your social platforms.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {products.map(p => (
                <div key={p.id} className="glass-light rounded-2xl p-4" data-testid={`ext-card-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full font-medium">{p.platform}</span>
                      <h4 className="font-semibold text-white mt-2">{p.title}</h4>
                      {p.description && <p className="text-sm text-slate-500 mt-1">{p.description}</p>}
                    </div>
                    <button onClick={() => remove(p.id)} className="p-1.5 text-slate-500 hover:text-red-400 rounded-lg">
                      <Trash className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5 text-sm">
                    <span className="flex items-center gap-1 text-amber-400 font-bold">
                      <Coins weight="fill" className="w-4 h-4" /> {p.price_tokens}
                    </span>
                    <span className="flex items-center gap-1 text-slate-500">
                      <Clock className="w-4 h-4" /> {p.duration_days}d
                    </span>
                    <span className="text-slate-500 text-xs">{p.orders_count} orders</span>
                    {p.has_auto_link && (
                      <span className="flex items-center gap-1 text-green-400 text-xs">
                        <CheckCircle className="w-3 h-3" /> Auto
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {section === 'orders' && (
        <div className="space-y-3">
          {orders.length === 0 ? (
            <div className="glass-light rounded-2xl p-12 text-center">
              <PaperPlaneTilt className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">No incoming orders yet.</p>
            </div>
          ) : (
            orders.map(o => (
              <div key={o.id} className="glass-light rounded-2xl p-4" data-testid={`order-${o.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full">{o.platform}</span>
                    <p className="text-white font-medium mt-1">{o.title}</p>
                    <p className="text-sm text-slate-500">Buyer: {o.buyer_name} · {o.price_tokens} tokens</p>
                  </div>
                  {o.delivered ? (
                    <span className="flex items-center gap-1 text-green-400 text-sm">
                      <CheckCircle weight="fill" className="w-4 h-4" /> Delivered
                    </span>
                  ) : deliveringId === o.id ? (
                    <div className="w-full space-y-2 mt-2">
                      <Input value={deliverLink} onChange={e => setDeliverLink(e.target.value)}
                        placeholder="https://..."
                        className="bg-slate-800 border-white/10" data-testid={`deliver-link-${o.id}`} />
                      <Textarea value={deliverMsg} onChange={e => setDeliverMsg(e.target.value)}
                        placeholder="Optional note to the buyer..." className="bg-slate-800 border-white/10" />
                      <div className="flex gap-2">
                        <button onClick={() => deliver(o.id)}
                          className="flex-1 py-2 bg-green-500 text-white rounded-lg font-semibold hover:bg-green-600"
                          data-testid={`send-${o.id}`}>
                          Send
                        </button>
                        <button onClick={() => { setDeliveringId(null); setDeliverLink(''); setDeliverMsg(''); }}
                          className="flex-1 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700">
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setDeliveringId(o.id)}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm font-semibold hover:bg-blue-600 flex items-center gap-1"
                      data-testid={`deliver-${o.id}`}>
                      <PaperPlaneTilt className="w-4 h-4" /> Deliver Link
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default ExternalSubsTab;
